import { Callout } from '@/components/Callout/Callout';
import { TemplateCard } from '@/components/TemplateCard/TemplateCard';
import type { MessageTemplate } from '@/graphql/message-templates';

import styles from './TemplateGrid.module.css';

export interface TemplateGridProps {
  templates: readonly MessageTemplate[];
  loading?: boolean;
  /** A refusal about one of the two lists. Both can fail independently, so
   *  this is a list of them rather than one string. */
  errors?: readonly string[];
  deletedIds?: ReadonlySet<string>;
  onOpen: (id: string) => void;
  /** Move one row one place in its own list. Absent when the listing cannot
   *  reorder at all, which is what takes the controls off every row. */
  onMove?: (id: string, direction: 'up' | 'down') => void;
  /** Whether a row has a visible neighbour to move past, per row and per
   *  direction. The container owns it because the answer is about the whole
   *  list — including the rows the filters have hidden — and the grid only
   *  holds what is on screen. */
  canMove?: (id: string, direction: 'up' | 'down') => boolean;
  /** The list a reorder is in flight for. Every row in it is being written, so
   *  they all stop taking clicks together. */
  reorderingList?: 'mine' | 'brokerage' | null;
}

/**
 * The listing: one scrolling box, both scopes merged into it.
 *
 * Merged here rather than at Keystone, which deliberately answers with two
 * lists and no precedence rule between them — that is the client's decision,
 * and this client makes it by showing one filterable listing with a chip per
 * row saying which scope a template is in.
 *
 * It scrolls inside itself rather than growing the page, so the filters stay
 * put while the list moves under them.
 *
 * The order it renders in is the one Keystone ranks by — `(position,
 * createdAt, id)`, applied by the container — and each list is ordered within
 * itself. Reordering across the merge is not a thing to offer: the two lists
 * are two lists to Keystone, with one order each and no precedence rule
 * between them, which is exactly the decision it left to the client.
 */
export function TemplateGrid({
  templates,
  loading = false,
  errors = [],
  deletedIds,
  onOpen,
  onMove,
  canMove,
  reorderingList = null,
}: TemplateGridProps) {
  return (
    <div className={styles.frame}>
      {errors.map((error) => (
        <Callout key={error} tone="danger" title="Keystone refused a list">
          {error}
        </Callout>
      ))}

      {templates.length === 0 ? (
        <p className={`${styles.empty} ${loading ? styles.loading : ''}`} role="status">
          {loading ? 'Asking Keystone…' : 'No templates match these filters.'}
        </p>
      ) : (
        <ul aria-busy={loading} aria-label="Message templates" className={styles.list}>
          {templates.map((template) => (
            <TemplateCard
              canMoveDown={canMove?.(template.id, 'down') ?? false}
              canMoveUp={canMove?.(template.id, 'up') ?? false}
              deleted={deletedIds?.has(template.id) ?? false}
              key={template.id}
              onMove={onMove ? (direction) => onMove(template.id, direction) : undefined}
              onOpen={onOpen}
              reordering={
                reorderingList !== null &&
                reorderingList === (template.tenantId !== null ? 'brokerage' : 'mine')
              }
              template={template}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
